import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { IMainState } from 'store/states'
import { IBlockInstanceModel } from 'models/block.model'
import * as fromActions from 'store/actions/blocks.action'
import { SurveyTemplatePicker } from 'pages/administration/SurveyBank/components/SurveyTemplatePicker'
import { SaveToBankModal } from 'pages/administration/SurveyBank/components/SaveToBankModal'
import type { SurveyTemplateRow } from 'pages/administration/SurveyBank/common/types'
import { TRANSLATION_PREFIX as BANK_PREFIX } from 'pages/administration/SurveyBank/constants'
import SurveyQuestions from './components/survey-questions/SurveyQuestions'
import SurveyDetails from './components/survey-details/SurveyDetails'
import { SurveyStartPath } from './components/survey-start-path'
import type { SurveyStartPathValue } from './components/survey-start-path'
import { useSurveyBankActions } from './useSurveyBankActions'
import { SurveyEditorShell } from './components/editor-shell'
import { ToolbarButton } from './components/question-rows/styled'

interface ISurvey {
  onCancel: () => void
  editing: boolean
}

const detailDataDefaultValues = {
  name: '',
  description: '',
  mandatory: true,
}

const sameText = (saved?: string | null, current?: string | null) =>
  (saved ?? '') === (current ?? '')

const detailsChanged = (
  saved: IBlockInstanceModel,
  current: IBlockInstanceModel
) =>
  !sameText(saved.name, current.name) ||
  !sameText(saved.description, current.description) ||
  saved.mandatory !== current.mandatory

const errors = {
  title: '',
  description: '',
  mandatory: '',
  questionTitle: '',
  questionType: '',
}

const Survey: React.FC<ISurvey> = ({ onCancel, editing }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { edit } = useSelector(({ blocks }: IMainState) => blocks)
  const [currentStep, setCurrentStep] = useState<1 | 2>(1)
  const [detailData, setDetailData] = useState(
    detailDataDefaultValues as IBlockInstanceModel
  )
  const savedDetails = useRef(detailData)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [saveOpen, setSaveOpen] = useState(false)
  const [startPath, setStartPath] = useState<SurveyStartPathValue | null>(null)
  const bank = useSurveyBankActions(edit)
  const prefix = 'administration.surveyEditor'
  const showChooser = bank.canUseBank
  const showDetails =
    startPath === 'create' || (bank.questionsReady && !showChooser)
  // Loading and a failed count are the same for the admin: not an empty
  // survey and not one that already has questions, so Next cannot skip ahead.
  const detailsLocked =
    (Boolean(edit.id) && !bank.questionsReady) ||
    (showChooser && startPath !== 'create')

  const handleApply = async (row: SurveyTemplateRow) => {
    if (await bank.applyTemplate(row)) {
      setPickerOpen(false)
      setCurrentStep(2)
    }
  }

  const handleSaveToBank = async (values: {
    name: string
    description: string
  }) => {
    if (await bank.saveToBank(values)) setSaveOpen(false)
  }

  useEffect(() => {
    const loaded = { ...detailDataDefaultValues, ...edit }
    setDetailData(loaded)
    savedDetails.current = loaded
  }, [edit])

  const handleNextStep = () => {
    if (detailsLocked) return
    if (edit.id) {
      if (detailsChanged(savedDetails.current, detailData)) {
        dispatch({
          type: fromActions.EDIT_SURVEY_BLOCK_DETAILS,
          payload: detailData,
        })
        savedDetails.current = detailData
      }
      setCurrentStep(2)
    } else {
      onCancel()
    }
  }

  const handleStartPath = (path: SurveyStartPathValue) => {
    setStartPath(path)
    if (path === 'template') setPickerOpen(true)
  }

  return (
    <>
      <SurveyEditorShell
        title={editing ? t(`${prefix}.edit`) : t(`${prefix}.new`)}
        secondaryLabel={
          currentStep === 1
            ? t(`${prefix}.buttons.close`)
            : t(`${prefix}.buttons.previous`)
        }
        primaryLabel={
          currentStep === 1
            ? t(`${prefix}.buttons.next`)
            : t(`${prefix}.buttons.finalize`)
        }
        primaryTestId="survey-block-primary"
        primaryDisabled={currentStep === 1 && detailsLocked}
        onSecondary={() => (currentStep === 1 ? onCancel() : setCurrentStep(1))}
        onPrimary={() => (currentStep === 1 ? handleNextStep() : onCancel())}
      >
        {currentStep === 1 ? (
          <>
            {showChooser && (
              <SurveyStartPath value={startPath} onChange={handleStartPath} />
            )}
            {showDetails && (
              <SurveyDetails
                values={detailData}
                onChange={setDetailData}
                errors={errors}
              />
            )}
          </>
        ) : (
          <SurveyQuestions
            blockId={edit.id}
            instanceId={edit.instanceId}
            toolbar={
              bank.canSaveToBank ? (
                <ToolbarButton
                  type="button"
                  data-testid="survey-block-save-to-bank"
                  onClick={() => {
                    bank.clearSaveNameError()
                    setSaveOpen(true)
                  }}
                >
                  {t(`${BANK_PREFIX}.block.saveToBank`)}
                </ToolbarButton>
              ) : null
            }
          />
        )}
      </SurveyEditorShell>
      {pickerOpen && (
        <SurveyTemplatePicker
          applying={bank.applying}
          onConfirm={handleApply}
          onClose={() => {
            setPickerOpen(false)
            if (startPath === 'template') setStartPath(null)
          }}
        />
      )}
      {saveOpen && (
        <SaveToBankModal
          initialName={detailData.name ?? ''}
          initialDescription={detailData.description ?? ''}
          saving={bank.saving}
          nameError={bank.saveNameError}
          onConfirm={handleSaveToBank}
          onClose={() => setSaveOpen(false)}
        />
      )}
    </>
  )
}

export default Survey
